import * as React from "react";
// @ts-ignore
import { ReactComponent as RequiredIcon } from "../../assets/required-form.svg";
// @ts-ignore
import { ReactComponent as PasswordIcon } from "../../assets/password-icon.svg";
import Show from "../Show";
import classnames from "classnames";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import moment from "moment";
import { BYU_OR_TSEL_NUMBER_PREFIX } from "../../../../../data";
import { func } from "../../../../../utils";
// @ts-ignore
import CalendarIcon from "../../assets/icon-calendar.svg";

type InputTypeProps = "text" | "password" | "number" | "email" | "date";

type ValidationType = "email" | "required" | "password" | "phone_number" | "byu_or_tsel_number";

type CheckingValidationType = "onTyping" | "onBlur";

interface Props {
  label: string;
  value: string;
  name?: string;
  placeholder: string;
  onChangeValue: (value: string, name: string) => void;
  onErrortypingValidation?: (error: string) => void;
  checkingValidationType?: CheckingValidationType;
  validation?: ValidationType[];
  /* Error message ketika button di click */
  errorMessage?: string;
  isRequired?: boolean;
  disabled?: boolean;
  type: InputTypeProps;
  maxLength?: number;
  description?: string;
  onBlur?: (value: any, error: string, name: string) => void;
}

const TextInputFormRegistration: React.FC<Props> = ({
  label,
  name = "",
  value,
  onBlur,
  onChangeValue,
  onErrortypingValidation,
  checkingValidationType = "onBlur",
  placeholder,
  errorMessage = "",
  isRequired = false,
  validation = [],
  disabled = false,
  type = "text",
  maxLength = 999,
  description = "",
}) => {
  const [isFocus, setIsFocus] = React.useState<boolean>(false);
  const [inputType, setInputType] = React.useState<InputTypeProps>(type);
  const [typingValidationError, setTypingValidationError] = React.useState<string>();

  const handleShowPassword = React.useCallback(() => {
    if (inputType === "text") return setInputType("password");

    return setInputType("text");
  }, [inputType]);

  const validateEmail = (value: string) => {
    const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return pattern.test(value);
  };

  const handleClickBuyByuNumber = () => {
    window.open("https://www.byu.id/v2", "_blank");
    setIsFocus(false);
    onBlur?.(value?.trim(), typingValidationError as string, name);
    setTypingValidationError("");
  };

  const handleValidation = (value: string) => {
    let errorMessage: string[] = [];

    validation?.forEach((item) => {
      if (item === "email") {
        if (!validateEmail(value) && value.trim().length > 0) {
          errorMessage.push("Pastikan email kamu valid");
        }
      }

      if (item === "required") {
        if (value.trim().length <= 0) errorMessage.push(`Pertanyaan wajib diisi`);
      }

      if (item === "password") {
        if (value?.trim()?.length < 8) {
          errorMessage.push("Password minimal memiliki 8 karakter");
        }
      }

      if (item === "phone_number") {
        if (value?.trim()?.length > 0) {
          if (value?.trim()?.length < 10) {
            errorMessage.push("Nomor handphone harus memiliki 10 - 14 karakter");
          }
        }
      }
      if (item === "byu_or_tsel_number") {
        if (value?.trim()?.length >= 4) {
          if (!BYU_OR_TSEL_NUMBER_PREFIX.some((prefix) => value?.trim().startsWith(prefix))) {
            return errorMessage.push("Nomor yang dimasukkan harus nomor Telkomsel atau by.U");
          }
          if (value?.trim()?.length < 10) {
            return errorMessage.push("Nomor handphone harus memiliki 10 - 14 karakter");
          }
        }
      }
    });

    onErrortypingValidation?.(errorMessage?.[0]);

    return errorMessage;
  };

  const isErrorTselNumber =
    typingValidationError === "Nomor yang dimasukkan harus nomor Telkomsel atau by.U";

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value ?? "";

    const isHaveTypingValidation = validation?.length > 0 && checkingValidationType === "onTyping";

    if (isHaveTypingValidation) {
      const errorMsg = handleValidation(value);
      setTypingValidationError?.(errorMsg?.[0]);
    }
    onChangeValue(value, name);
  };

  const handleChangeValueNumber = (values: NumberFormatValues) => {
    const value = values.formattedValue ?? 0;

    const isHaveTypingValidation = validation?.length > 0 && checkingValidationType === "onTyping";

    if (isHaveTypingValidation) {
      const errorMsg = handleValidation(String(value));
      setTypingValidationError?.(errorMsg?.[0]);
    }
    onChangeValue(String(value), name);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    // @ts-ignore
    let pastedText = e.clipboardData.getData("text");
    let sanitized = pastedText.replace(/\D/g, "");

    onChangeValue(String(sanitized), name);
  };

  const handleOnBlur = () => {
    if (isErrorTselNumber) {
      return;
    }
    setIsFocus(false);
    onBlur?.(value?.trim(), typingValidationError as string, name);
    setTypingValidationError("");
  };

  const handleBorderColor = () => {
    if (isFocus) return "border-[#00BCF1]";
    if (!isFocus && !errorMessage) return "border-[#B8C1CB]";
    if (!isFocus && !!errorMessage) return "border-red-600";
  };

  const currentDate = moment()?.format("YYYY-MM-DD");

  return (
    <div className="flex flex-col py-[8px] px-[24px] gap-y-[8px]">
      <div className="flex flex-col gap-y-1">
        <div className="flex gap-x-[2px]">
          <h1 className="text-[13px] text-[#6A7481] leading-none font-semibold">{label}</h1>
          <Show when={isRequired}>
            <RequiredIcon className="w-[10 px] h-[10px]" />
          </Show>
        </div>
        <Show when={!!description}>
          <p className="text-[12px] text-[#1F2D3D]">{description}</p>
        </Show>
      </div>
      <div>
        <div
          className={classnames(
            "flex justify-between items-center border-[1px] bg-white rounded-[4px] px-[8px] h-[36px]",
            handleBorderColor(),
          )}
        >
          <Show when={type === "number"}>
            <NumericFormat
              pattern="\d*"
              placeholder={placeholder}
              className="outline:none w-full disabled:bg-white disabled:text-[red]"
              disabled={disabled}
              maxLength={maxLength}
              onFocus={() => setIsFocus(true)}
              onBlur={handleOnBlur}
              value={value}
              allowLeadingZeros
              allowNegative={false}
              onPaste={handlePaste}
              renderText={(formmattedValue: string, props) => {
                return formmattedValue?.replace("-", "");
              }}
              onValueChange={handleChangeValueNumber}
            />
          </Show>
          <Show when={type === "date"}>
            <div className="flex cursor-pointer w-[100%] justify-between">
              <input
                type={"date"}
                className="outline:none disabled:bg-white disabled:text-[#B8C1CB] w-[100%]"
                value={value}
                onChange={disabled ? undefined : handleChangeValue}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => setIsFocus(true)}
                onBlur={handleOnBlur}
                maxLength={maxLength}
              />
            </div>
          </Show>
          <Show when={!["number", "date"]?.includes(type)}>
            <input
              type={inputType}
              className="outline:none w-full disabled:bg-white disabled:text-[#B8C1CB]"
              value={value}
              onChange={disabled ? undefined : handleChangeValue}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setIsFocus(true)}
              onBlur={handleOnBlur}
              maxLength={maxLength}
            />
          </Show>

          <Show when={type === "password"}>
            <PasswordIcon onClick={disabled ? undefined : handleShowPassword} />
          </Show>
        </div>
        <Show when={!isFocus}>
          <span className={`text-[11px] ${"text-red-600"}`}>{errorMessage}</span>
        </Show>
        <span className={`text-[11px] ${isErrorTselNumber ? "text-red-600" : "text-[#1F2D3D]"}`}>
          {typingValidationError}
        </span>
        <Show when={isErrorTselNumber}>
          <div
            onClick={handleClickBuyByuNumber}
            className="bg-[#00BCF1] flex justify-between items-center w-[max-content] py-[4px] px-[16px] my-[8px] rounded-[16px] cursor-pointer"
          >
            <p className="text-[14px] text-white font-semibold">Beli Nomor by.U Sekarang</p>
            <img
              src={func.getStaticImg("/icon/right-arrow-white.svg")}
              style={{
                height: 16,
                width: 20,
                marginLeft: 8,
              }}
            />
          </div>
        </Show>
      </div>
    </div>
  );
};

export default TextInputFormRegistration;
